import React from 'react';
import { useApp } from '../../context/AppContext';
import { 
  AlertTriangle, 
  Clock, 
  CheckCircle2,
  Activity,
  Shield
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

// MALA'IKA Branding Colors
const MALAIKA_COLORS = {
  navy: '#1B2A4A',
  red: '#E74C3C',
  green: '#2ECC71',
  dark: '#0F1923'
};

const TYPE_COLORS = {
  'Medical': '#E74C3C',
  'Fall': '#F97316',
  'Security': 'var(--color-burgundy-500)',
  'Distress': '#3B82F6',
  'Fire': '#EF4444',
  'Unknown': '#6B7280'
};

const monthKey = (value) => {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return null;
  return `${date.getFullYear()}-${date.getMonth()}`;
};

const lastSixMonths = () => {
  const now = new Date();
  return Array.from({ length: 6 }, (_, index) => {
    const date = new Date(now.getFullYear(), now.getMonth() - (5 - index), 1);
    return {
      key: monthKey(date),
      month: date.toLocaleString('en-US', { month: 'short' }),
    };
  });
};

const EmergencyAnalyticsPage = () => {
  const { emergencies } = useApp();

  // Calculate stats
  const totalEmergencies = emergencies.length;
  const resolvedEmergencies = emergencies.filter(e => e.status === 'Resolved').length;
  const responseTimes = emergencies
    .map(e => Number(e.responseTime ?? e.response_time))
    .filter(value => Number.isFinite(value) && value >= 0);
  const avgResponseTime = responseTimes.length > 0
    ? Math.round(responseTimes.reduce((sum, value) => sum + value, 0) / responseTimes.length)
    : 0;
  const falseAlarms = emergencies.filter(e => e.type === 'Security' && e.resolution?.includes('False alarm')).length;

  // Pie chart data for emergency types
  const typeData = Object.entries(
    emergencies.reduce((acc, e) => {
      acc[e.type] = (acc[e.type] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value, color: TYPE_COLORS[name] || '#6B7280' }));

  const responseTrend = lastSixMonths().map(month => {
    const values = emergencies
      .filter(e => monthKey(e.triggeredAt || e.triggered_at || e.createdAt || e.created_at) === month.key)
      .map(e => Number(e.responseTime ?? e.response_time))
      .filter(value => Number.isFinite(value) && value >= 0);
    return {
      month: month.month,
      time: values.length ? Math.round(values.reduce((sum, value) => sum + value, 0) / values.length) : 0,
    };
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Resolved':
        return 'bg-green-100 text-green-700';
      case 'Triggered':
      case 'Acknowledged':
        return 'bg-yellow-100 text-yellow-700';
      case 'Dispatched':
      case 'On Scene':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="emergency-analytics-page">
      {/* Header with MALA'IKA branding accent */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: MALAIKA_COLORS.navy }}
              >
                <Shield className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="font-serif text-2xl font-bold text-gray-900">Emergency Analytics</h1>
                <p className="text-gray-500 mt-1">
                  MALA'IKA Command Center Response Data
                </p>
              </div>
            </div>
            <span 
              className="px-3 py-1.5 text-sm font-medium rounded-full text-white"
              style={{ backgroundColor: MALAIKA_COLORS.red }}
            >
              Powered by MALA'IKA
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
            </div>
            <p className="text-3xl font-serif font-bold text-gray-900">{totalEmergencies}</p>
            <p className="text-sm text-gray-500">Total Emergencies</p>
            <p className="text-xs text-gray-400 mt-1">This quarter</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
            </div>
            <p className="text-3xl font-serif font-bold text-green-600">{avgResponseTime}s</p>
            <p className="text-sm text-gray-500">Avg Response Time</p>
            <p className="text-xs text-gray-400 mt-1">{responseTimes.length} measured events</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-3xl font-serif font-bold text-blue-600">
              {totalEmergencies > 0 ? Math.round((resolvedEmergencies / totalEmergencies) * 100) : 0}%
            </p>
            <p className="text-sm text-gray-500">Resolution Rate</p>
            <p className="text-xs text-gray-400 mt-1">{resolvedEmergencies} of {totalEmergencies} resolved</p>
          </div>
          
          <div className="bg-white rounded-2xl border border-gray-100 p-5">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
                <Activity className="w-5 h-5 text-yellow-600" />
              </div>
            </div>
            <p className="text-3xl font-serif font-bold text-yellow-600">
              {totalEmergencies > 0 ? Math.round((falseAlarms / totalEmergencies) * 100) : 0}%
            </p>
            <p className="text-sm text-gray-500">False Alarm Rate</p>
            <p className="text-xs text-gray-400 mt-1">{falseAlarms} false alarms</p>
          </div>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Emergency Types */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Emergency Types</h3>
            </div>
            <div className="p-5">
              <div className="h-[250px] flex items-center justify-center">
                {typeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={typeData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={90}
                        paddingAngle={4}
                        dataKey="value"
                      >
                        {typeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-gray-400">No data available</p>
                )}
              </div>
              <div className="flex flex-wrap justify-center gap-4 mt-4">
                {typeData.map((item) => (
                  <div key={item.name} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                    <span className="text-sm text-gray-600">{item.name} ({item.value})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Response Time Trend */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h3 className="font-serif text-lg font-semibold text-gray-900">Response Time Trend</h3>
            </div>
            <div className="p-5">
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={responseTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="month" stroke="#9ca3af" fontSize={12} />
                    <YAxis stroke="#9ca3af" fontSize={12} unit="s" />
                    <Tooltip 
                      contentStyle={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px' }}
                      formatter={(value) => [`${value} seconds`, 'Avg Response']}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="time" 
                      stroke={MALAIKA_COLORS.green} 
                      strokeWidth={3}
                      dot={{ fill: MALAIKA_COLORS.green, strokeWidth: 2 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Emergency Events */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-serif text-lg font-semibold text-gray-900">Recent Emergency Events</h3>
            <span className="text-sm text-gray-500">{emergencies.length} total events</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Event</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dependant</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Triggered By</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Response Time</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {emergencies.map((emergency) => (
                  <tr key={emergency.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${TYPE_COLORS[emergency.type]}20` }}
                        >
                          <AlertTriangle className="w-5 h-5" style={{ color: TYPE_COLORS[emergency.type] }} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{emergency.id}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(emergency.triggeredAt).toLocaleDateString('en-US', { 
                              month: 'short', day: 'numeric', year: 'numeric' 
                            })}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span 
                        className="px-2 py-1 text-xs font-medium rounded-full"
                        style={{ 
                          backgroundColor: `${TYPE_COLORS[emergency.type]}20`,
                          color: TYPE_COLORS[emergency.type]
                        }}
                      >
                        {emergency.type}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="text-gray-900">{emergency.dependantName}</p>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {emergency.triggeredBy}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-medium text-green-600">{emergency.responseTime}s</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(emergency.status)}`}>
                        {emergency.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {emergencies.length === 0 && (
            <div className="p-12 text-center">
              <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-900">No emergency events</h3>
              <p className="text-sm text-gray-500">Emergency events will appear here</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmergencyAnalyticsPage;
